import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tab,
  TablePagination,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import {
  closeCashCounter,
  convertCashCounterPayment,
  deleteCashCounterLedgerEntry,
  getCashCounterLedger,
  getCashCounterPrintData,
  getCashCounterSummary,
  getCashCounterTimeline,
  openCashCounter,
  updateCashCounterLedgerEntry,
  withdrawCashCounter,
} from '../../../services/api';
import { formatCurrency } from '../../../shared/utils/formatters';
import { downloadBlob } from '../../../utils/downloadBlob';
import { detectThemeSync } from '../../../utils/themeUtils';
import {
  formatHotelDate,
  formatHotelDateTime,
  formatHotelDateTimeParts,
  getHotelDateTimeFormat,
  getHotelNowMoment,
  getHotelTimezone,
} from '../../../utils/dateUtils';
import {
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerTextFieldSx,
} from '../../../utils/bookingDrawerLayout';
import { getDrawerSelectMenuProps } from '../../../utils/drawerOverlayProps';
import CashCollectionBreakdown from './CashCollectionBreakdown';
import CashCounterDrawerShell from './CashCounterDrawerShell';
import { CASH_COUNTER_SHIFTS, getShiftLabel, getSuggestedShift } from './cashCounterShifts';

function formatCashCounterDateTime(value, hotelTimezone) {
  if (!value) return '—';
  const formatted = formatHotelDateTime(value, hotelTimezone || getHotelTimezone());
  return formatted || '—';
}

function formatCashCounterTimelineAt(ev, hotelTimezone) {
  const tz = hotelTimezone || getHotelTimezone();
  if (ev?.at) return formatCashCounterDateTime(ev.at, tz);
  if (ev?.at_display) {
    const m = tz && moment.tz
      ? moment.tz(ev.at_display, 'YYYY-MM-DD HH:mm', tz)
      : moment(ev.at_display, 'YYYY-MM-DD HH:mm', true);
    if (m.isValid()) return m.format(getHotelDateTimeFormat());
    return ev.at_display;
  }
  return '—';
}

function formatCashCounterLedgerWhen(row, hotelTimezone) {
  if (row?.entry_at) {
    const parts = formatHotelDateTimeParts(row.entry_at, hotelTimezone || getHotelTimezone());
    return {
      date: parts.date || formatHotelDate(row.date) || row.date || '—',
      time: parts.time || row.time || '—',
    };
  }
  return {
    date: row?.date ? (formatHotelDate(row.date) || row.date) : '—',
    time: row?.time || '—',
  };
}
const ACCENT = '#4ce1b6';

const DATE_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7_days', label: 'Last 7 Days' },
  { key: 'custom', label: 'Custom' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'debit_card', label: 'Debit Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const ENTRY_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'opening_balance', label: 'Opening Balance' },
  { value: 'cash_payment', label: 'Cash Payment' },
  { value: 'refund', label: 'Refund' },
  { value: 'withdrawal', label: 'Withdrawal' },
  { value: 'payment_conversion', label: 'Payment Conversion' },
  { value: 'manual_adjustment', label: 'Manual Adjustment' },
  { value: 'closing_balance', label: 'Closing Balance' },
];

const CONFIRM_ACTION_COPY = {
  open: {
    title: 'Open cash counter?',
    message: 'This will start a fresh counter session with the opening cash you entered.',
    confirmLabel: 'Open Counter',
  },
  close: {
    title: 'Close cash counter?',
    message: 'This will lock the current session and save the closing cash count.',
    confirmLabel: 'Close Counter',
  },
  withdraw: {
    title: 'Withdraw cash?',
    message: 'This will record the withdrawal and update the cash available in the counter.',
    confirmLabel: 'Withdraw',
  },
  convert: {
    title: 'Save conversion?',
    message: 'This will reduce cash in the counter and record the amount received by the selected method.',
    confirmLabel: 'Convert Cash',
  },
};

function formatOpeningCashInput(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return '';
  return parsed.toFixed(2);
}

function resolveOpenCounterDefaults(summary) {
  const suggested = summary?.suggested_opening;
  if (Number(suggested?.total || 0) > 0) {
    return {
      opening_hotel_cash: formatOpeningCashInput(suggested.hotel),
      opening_restaurant_cash: formatOpeningCashInput(suggested.restaurant),
    };
  }

  const cashAvailable = Number(summary?.cash_available || 0);
  const breakdown = summary?.opening_breakdown;
  if (cashAvailable > 0 && breakdown) {
    const hotelBase = Number(breakdown.hotel || 0);
    const restaurantBase = Number(breakdown.restaurant || 0);
    const baseTotal = hotelBase + restaurantBase;
    if (baseTotal > 0) {
      const hotel = Math.round(((cashAvailable * hotelBase) / baseTotal) * 100) / 100;
      const restaurant = Math.round((cashAvailable - hotel) * 100) / 100;
      return {
        opening_hotel_cash: formatOpeningCashInput(hotel),
        opening_restaurant_cash: formatOpeningCashInput(restaurant),
      };
    }
    return {
      opening_hotel_cash: formatOpeningCashInput(cashAvailable),
      opening_restaurant_cash: '0.00',
    };
  }

  return { opening_hotel_cash: '', opening_restaurant_cash: '' };
}

function splitCashByOpeningBreakdown(amount, openingBreakdown) {
  const total = Number(amount || 0);
  if (total <= 0) {
    return { hotel: 0, restaurant: 0, total: 0 };
  }
  const hotelBase = Number(openingBreakdown?.hotel || 0);
  const restaurantBase = Number(openingBreakdown?.restaurant || 0);
  const baseTotal = hotelBase + restaurantBase;
  if (baseTotal > 0) {
    const hotel = Math.round(((total * hotelBase) / baseTotal) * 100) / 100;
    return {
      hotel,
      restaurant: Math.round((total - hotel) * 100) / 100,
      total,
    };
  }
  return { hotel: total, restaurant: 0, total };
}

const TIMELINE_TYPE_COLORS = {
  cash_payment_received: '#22c55e',
  refund: '#f87171',
  cash_withdrawn: '#f97316',
  payment_converted: '#38bdf8',
  manual_adjustment: '#a78bfa',
  counter_opened: ACCENT,
  counter_closed: '#94a3b8',
};

function eventSortKey(ev) {
  if (!ev?.at) return 0;
  try {
    return new Date(ev.at).getTime();
  } catch {
    return 0;
  }
}

/** Group flat timeline events into one box per counter session. */
function groupTimelineBySession(events) {
  const sessionMap = new Map();
  const orphans = [];

  (events || []).forEach((ev) => {
    const sid = ev.session_id;
    if (!sid) {
      orphans.push(ev);
      return;
    }
    if (!sessionMap.has(sid)) {
      sessionMap.set(sid, {
        sessionId: sid,
        open: null,
        close: null,
        transactions: [],
        isCurrent: false,
        sessionSummary: null,
      });
    }
    const group = sessionMap.get(sid);
    if (ev.session_summary) group.sessionSummary = ev.session_summary;
    if (ev.is_current) group.isCurrent = true;
    if (ev.type === 'counter_opened') group.open = ev;
    else if (ev.type === 'counter_closed') group.close = ev;
    else group.transactions.push(ev);
  });

  const sessions = Array.from(sessionMap.values()).map((group) => {
    const transactions = [...group.transactions].sort((a, b) => eventSortKey(a) - eventSortKey(b));
    const latestAt = Math.max(
      eventSortKey(group.close),
      eventSortKey(group.open),
      ...transactions.map(eventSortKey),
      0,
    );
    return { ...group, transactions, latestAt };
  });

  sessions.sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    return b.latestAt - a.latestAt;
  });

  orphans.sort((a, b) => eventSortKey(b) - eventSortKey(a));

  return [
    ...sessions.map((s) => ({ kind: 'session', ...s })),
    ...orphans.map((ev) => ({ kind: 'orphan', event: ev })),
  ];
}

function TimelineTxnRow({ ev, isDark, hotelTimezone }) {
  const accent = TIMELINE_TYPE_COLORS[ev.type] || ACCENT;
  const isIn = ev.direction === 'in' || Number(ev.credit || 0) > 0;
  const isOut = ev.direction === 'out' || Number(ev.debit || 0) > 0;
  const amountColor = isIn ? '#22c55e' : isOut ? '#f87171' : (isDark ? '#e2e8f0' : '#334155');
  const meta = [
    ev.payment_method_label,
    ev.source === 'hotel' ? 'Hotel' : ev.source === 'restaurant' ? 'Restaurant' : null,
  ].filter(Boolean);

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={1.5}
      sx={{
        py: 1,
        px: 1.25,
        borderRadius: 1.5,
        bgcolor: isDark ? alpha('#fff', 0.03) : alpha(accent, 0.05),
        border: `1px solid ${alpha(accent, 0.12)}`,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{ev.title}</Typography>
          {meta.map((label) => (
            <Chip key={`${ev.ledger_entry_id}-${label}`} size="small" label={label} sx={{ height: 20, fontSize: 11 }} />
          ))}
        </Stack>
        {(ev.reference_label || ev.guest_name || ev.description) ? (
          <Typography variant="caption" color="text.secondary" display="block" mt={0.35}>
            {[ev.reference_label, ev.guest_name, ev.description !== ev.title ? ev.description : null]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        ) : null}
      </Box>
      <Stack alignItems="flex-end" spacing={0.15} sx={{ flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {formatCashCounterTimelineAt(ev, hotelTimezone)}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 800, color: amountColor }}>
          {`${isIn ? '+' : isOut ? '−' : ''}${formatCurrency(ev.amount)}`}
        </Typography>
        {ev.running_balance != null ? (
          <Typography variant="caption" color="text.secondary">
            Bal {formatCurrency(ev.running_balance)}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

function SessionCloseSummary({ summary, isDark }) {
  if (!summary) return null;
  const hotelCollected = Number(summary.hotel_collected ?? summary.collection_breakdown?.hotel?.collected ?? 0);
  const restaurantCollected = Number(
    summary.restaurant_collected ?? summary.collection_breakdown?.restaurant?.collected ?? 0,
  );
  const totalCollected = Number(summary.total_collected ?? hotelCollected + restaurantCollected);
  const withdrawn = Number(summary.cash_withdrawn || 0);
  const sessionWithdrawn = Number(summary.session_withdrawn ?? 0);
  const closingWithdrawn = Number(summary.closing_withdrawn ?? 0);
  const converted = Number(summary.cash_converted || 0);
  const expected = Number(summary.expected_cash ?? 0);
  const actual = summary.actual_cash != null ? Number(summary.actual_cash) : null;
  const difference = summary.difference != null ? Number(summary.difference) : null;
  const opening = summary.opening_breakdown || {};
  const openingCash = Number(summary.opening_cash || 0);

  const withdrawSub = [
    sessionWithdrawn > 0 ? `Session ${formatCurrency(sessionWithdrawn)}` : null,
    closingWithdrawn > 0 ? `Close ${formatCurrency(closingWithdrawn)}` : null,
  ].filter(Boolean).join(' + ') || 'Cash out of counter';

  const cards = [
    {
      label: 'Hotel Collected',
      value: hotelCollected,
      color: '#38bdf8',
      sub: 'Booking & front desk',
    },
    {
      label: 'Restaurant Collected',
      value: restaurantCollected,
      color: '#f59e0b',
      sub: 'POS cash',
    },
    {
      label: 'Withdrawn',
      value: withdrawn,
      color: '#f97316',
      sub: withdrawSub,
    },
    {
      label: converted > 0 ? 'Converted' : (actual != null ? 'Actual Counted' : 'Expected Total'),
      value: converted > 0 ? converted : (actual != null ? actual : expected),
      color: converted > 0 ? '#38bdf8' : ACCENT,
      sub: converted > 0
        ? 'Cash → other method'
        : (actual != null ? `Expected ${formatCurrency(expected)}` : 'Session float'),
    },
  ];

  if (converted > 0) {
    cards.push({
      label: actual != null ? 'Actual Counted' : 'Expected Total',
      value: actual != null ? actual : expected,
      color: ACCENT,
      sub: actual != null ? `Expected ${formatCurrency(expected)}` : 'After withdraw + convert',
    });
  }

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: `1px solid ${alpha(ACCENT, isDark ? 0.2 : 0.28)}`,
        bgcolor: isDark ? alpha(ACCENT, 0.06) : alpha(ACCENT, 0.07),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.25} flexWrap="wrap" gap={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Close Summary
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Opening {formatCurrency(openingCash)}
          {' · '}
          Hotel {formatCurrency(opening.hotel)} · Restaurant {formatCurrency(opening.restaurant)}
        </Typography>
      </Stack>
      <Grid container spacing={1.25}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={converted > 0 ? 4 : 3} key={card.label} lg={converted > 0 ? true : 3}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${alpha(card.color, 0.28)}`,
                bgcolor: isDark ? alpha('#000', 0.2) : '#fff',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {card.label}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900, color: card.color, mt: 0.35 }}>
                {formatCurrency(card.value)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {card.sub}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Stack spacing={0.75} mt={1.5}>
        <Typography variant="body2" color="text.secondary">
          {formatCurrency(openingCash)}
          {' + Collected '}
          {formatCurrency(totalCollected)}
          {' − Withdrawn '}
          {formatCurrency(withdrawn)}
          {converted > 0 ? ` − Converted ${formatCurrency(converted)}` : ''}
          {' = Expected '}
          <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>{formatCurrency(expected)}</Box>
          {actual != null ? (
            <>
              {' · Actual '}
              <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>{formatCurrency(actual)}</Box>
            </>
          ) : null}
          {difference != null && difference !== 0 ? (
            <Box component="span" sx={{ fontWeight: 700, color: difference < 0 ? '#f87171' : '#22c55e' }}>
              {` · Diff ${formatCurrency(difference)}`}
            </Box>
          ) : null}
        </Typography>
      </Stack>
    </Box>
  );
}

function SessionTimelineCard({ group, isDark, hotelTimezone }) {
  const [txnPage, setTxnPage] = useState(0);
  const [txnRowsPerPage, setTxnRowsPerPage] = useState(10);

  useEffect(() => {
    setTxnPage(0);
  }, [group.sessionId]);

  const summary = group.sessionSummary
    || group.close?.session_summary
    || group.open?.session_summary
    || null;
  const shift = group.close?.shift || group.open?.shift || summary?.shift;
  const cashier = group.close?.cashier || group.open?.cashier || summary?.cashier_name;
  const isClosed = Boolean(group.close) || summary?.status === 'closed';
  const borderColor = group.isCurrent ? ACCENT : (isClosed ? '#94a3b8' : ACCENT);

  const sortedTxns = useMemo(
    () => [...(group.transactions || [])].sort((a, b) => eventSortKey(b) - eventSortKey(a)),
    [group.transactions],
  );
  const txnCount = sortedTxns.length;
  const paginatedTxns = useMemo(
    () => sortedTxns.slice(txnPage * txnRowsPerPage, txnPage * txnRowsPerPage + txnRowsPerPage),
    [sortedTxns, txnPage, txnRowsPerPage],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${alpha(borderColor, isDark ? 0.28 : 0.35)}`,
        bgcolor: isDark ? alpha('#fff', 0.02) : alpha(borderColor, 0.04),
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} mb={1.5}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {isClosed ? 'Counter Session' : 'Open Counter'}
            </Typography>
            {shift ? <Chip size="small" label={shift} sx={{ fontWeight: 700 }} /> : null}
            <Chip
              size="small"
              label={group.isCurrent ? 'Current' : isClosed ? 'Closed' : 'Open'}
              color={group.isCurrent ? 'success' : 'default'}
              sx={{ fontWeight: 700 }}
            />
            {txnCount > 0 ? (
              <Chip size="small" variant="outlined" label={`${txnCount} txn${txnCount === 1 ? '' : 's'}`} sx={{ fontWeight: 600 }} />
            ) : null}
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap mt={1}>
            {cashier ? (
              <Chip
                size="small"
                label={`Cashier · ${cashier}`}
                sx={{
                  fontWeight: 700,
                  height: 26,
                  bgcolor: isDark ? alpha('#818cf8', 0.22) : alpha('#6366f1', 0.14),
                  color: isDark ? '#c7d2fe' : '#4338ca',
                  border: `1px solid ${alpha('#6366f1', isDark ? 0.4 : 0.28)}`,
                  '& .MuiChip-label': { px: 1.1 },
                }}
              />
            ) : null}
            {(summary?.opened_at || group.open?.at || summary?.opened_at_display || group.open?.at_display) ? (
              <Chip
                size="small"
                label={`Opened · ${
                  (summary?.opened_at || group.open?.at)
                    ? formatCashCounterDateTime(summary?.opened_at || group.open?.at, hotelTimezone)
                    : (summary?.opened_at_display || group.open?.at_display)
                }`}
                sx={{
                  fontWeight: 700,
                  height: 26,
                  bgcolor: isDark ? alpha(ACCENT, 0.2) : alpha(ACCENT, 0.16),
                  color: isDark ? '#a7f3d0' : '#0f766e',
                  border: `1px solid ${alpha(ACCENT, isDark ? 0.45 : 0.4)}`,
                  '& .MuiChip-label': { px: 1.1 },
                }}
              />
            ) : null}
            {isClosed && (summary?.closed_at || group.close?.at || summary?.closed_at_display || group.close?.at_display) ? (
              <Chip
                size="small"
                label={`Closed · ${
                  (summary?.closed_at || group.close?.at)
                    ? formatCashCounterDateTime(summary?.closed_at || group.close?.at, hotelTimezone)
                    : (summary?.closed_at_display || group.close?.at_display)
                }`}
                sx={{
                  fontWeight: 700,
                  height: 26,
                  bgcolor: isDark ? alpha('#94a3b8', 0.22) : alpha('#64748b', 0.12),
                  color: isDark ? '#e2e8f0' : '#334155',
                  border: `1px solid ${alpha('#64748b', isDark ? 0.4 : 0.28)}`,
                  '& .MuiChip-label': { px: 1.1 },
                }}
              />
            ) : null}
            {summary?.duration_label ? (
              <Chip
                size="small"
                label={summary.duration_label}
                sx={{
                  fontWeight: 800,
                  height: 26,
                  bgcolor: isDark ? alpha('#f59e0b', 0.22) : alpha('#f59e0b', 0.16),
                  color: isDark ? '#fde68a' : '#b45309',
                  border: `1px solid ${alpha('#f59e0b', isDark ? 0.45 : 0.35)}`,
                  '& .MuiChip-label': { px: 1.1 },
                }}
              />
            ) : null}
          </Stack>
        </Box>
        {(group.open || summary) ? (
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant="caption" color="text.secondary">Opening float</Typography>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {formatCurrency(summary?.opening_cash ?? group.open?.amount ?? 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Hotel {formatCurrency(summary?.opening_breakdown?.hotel ?? group.open?.opening_hotel_cash)}
              {' · '}
              Restaurant {formatCurrency(summary?.opening_breakdown?.restaurant ?? group.open?.opening_restaurant_cash)}
            </Typography>
          </Box>
        ) : null}
      </Stack>

      {/* Summary first so it stays visible even with 100+ transactions */}
      {isClosed ? (
        <Box mb={1.5}>
          <SessionCloseSummary summary={summary || group.close?.session_summary} isDark={isDark} />
        </Box>
      ) : summary ? (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: `1px dashed ${alpha(ACCENT, 0.35)}`,
            bgcolor: isDark ? alpha(ACCENT, 0.05) : alpha(ACCENT, 0.06),
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>
            LIVE SESSION TOTALS
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={1} flexWrap="wrap" useFlexGap>
            <Typography variant="body2">
              Hotel <strong>{formatCurrency(summary.hotel_collected)}</strong>
            </Typography>
            <Typography variant="body2">
              Restaurant <strong>{formatCurrency(summary.restaurant_collected)}</strong>
            </Typography>
            <Typography variant="body2">
              Collected <strong>{formatCurrency(summary.total_collected)}</strong>
            </Typography>
            <Typography variant="body2">
              Withdrawn <strong>{formatCurrency(summary.cash_withdrawn)}</strong>
            </Typography>
            {Number(summary.cash_converted || 0) > 0 ? (
              <Typography variant="body2">
                Converted <strong>{formatCurrency(summary.cash_converted)}</strong>
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ color: ACCENT, fontWeight: 800 }}>
              Expected {formatCurrency(summary.expected_cash)}
            </Typography>
          </Stack>
        </Box>
      ) : null}

      {txnCount > 0 ? (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.4 }}>
              TRANSACTIONS ({txnCount})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Newest first
            </Typography>
          </Stack>
          <Stack
            spacing={1}
            sx={{
              maxHeight: { xs: 360, sm: 420 },
              overflowY: 'auto',
              pr: 0.5,
              mb: 0.5,
            }}
          >
            {paginatedTxns.map((ev, idx) => (
              <TimelineTxnRow
                key={`${ev.ledger_entry_id || ev.at}-${txnPage}-${idx}`}
                ev={ev}
                isDark={isDark}
                hotelTimezone={hotelTimezone}
              />
            ))}
          </Stack>
          {txnCount > 10 ? (
            <TablePagination
              component="div"
              count={txnCount}
              page={txnPage}
              rowsPerPage={txnRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, page) => setTxnPage(page)}
              onRowsPerPageChange={(event) => {
                setTxnRowsPerPage(parseInt(event.target.value, 10));
                setTxnPage(0);
              }}
              labelRowsPerPage="Per page"
              sx={{
                borderTop: `1px solid ${alpha(ACCENT, isDark ? 0.12 : 0.1)}`,
                color: isDark ? '#cbd5e1' : '#475569',
                '& .MuiTablePagination-toolbar': { minHeight: 44, pl: 0 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { m: 0 },
                '& .MuiSvgIcon-root': { color: isDark ? '#cbd5e1' : '#475569' },
              }}
            />
          ) : null}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" py={1}>
          No transactions in this session for the selected period.
        </Typography>
      )}
    </Paper>
  );
}

function TimelineEventRow({ ev, isDark, hotelTimezone }) {
  const accent = TIMELINE_TYPE_COLORS[ev.type] || ACCENT;
  const isIn = ev.direction === 'in' || Number(ev.credit || 0) > 0;
  const isOut = ev.direction === 'out' || Number(ev.debit || 0) > 0;
  const amountColor = ev.type === 'counter_closed'
    ? (isDark ? '#e2e8f0' : '#334155')
    : isIn
      ? '#22c55e'
      : isOut
        ? '#f87171'
        : (isDark ? '#e2e8f0' : '#334155');

  const metaChips = [];
  if (ev.payment_method_label) metaChips.push(ev.payment_method_label);
  if (ev.source) metaChips.push(ev.source === 'hotel' ? 'Hotel' : 'Restaurant');
  if (ev.shift) metaChips.push(ev.shift);

  const secondaryLines = [
    ev.reference_label,
    ev.guest_name,
    ev.conversion_detail,
    ev.notes,
    ev.approved_by ? `Approved by ${ev.approved_by}` : null,
    ev.cashier ? `Cashier: ${ev.cashier}` : null,
    ev.created_by && ev.created_by !== ev.cashier ? `Recorded by ${ev.created_by}` : null,
  ].filter(Boolean);

  const showAmount = Number(ev.amount || 0) > 0 || ev.type === 'counter_closed';

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha(accent, 0.18)}`,
        bgcolor: isDark ? alpha('#fff', 0.02) : alpha(accent, 0.04),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography fontWeight={700}>{ev.title}</Typography>
            {metaChips.map((label) => (
              <Chip
                key={`${ev.at}-${label}`}
                size="small"
                label={label}
                sx={{
                  height: 22,
                  fontWeight: 600,
                  bgcolor: alpha(accent, isDark ? 0.14 : 0.12),
                  color: isDark ? '#e2e8f0' : '#334155',
                }}
              />
            ))}
          </Stack>
          {secondaryLines.length > 0 ? (
            <Typography variant="body2" color="text.secondary" mt={0.75}>
              {secondaryLines.join(' · ')}
            </Typography>
          ) : null}
          {ev.type === 'counter_opened' && (ev.opening_hotel_cash != null || ev.opening_restaurant_cash != null) ? (
            <Typography variant="body2" color="text.secondary" mt={0.75}>
              Hotel {formatCurrency(ev.opening_hotel_cash)} · Restaurant {formatCurrency(ev.opening_restaurant_cash)}
            </Typography>
          ) : null}
          {ev.type === 'counter_closed' ? (
            <SessionCloseSummary summary={ev.session_summary} isDark={isDark} />
          ) : null}
          {ev.description && ev.description !== ev.detail ? (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {ev.description}
            </Typography>
          ) : null}
        </Box>
        <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {formatCashCounterTimelineAt(ev, hotelTimezone)}
          </Typography>
          {showAmount && ev.type !== 'counter_closed' ? (
            <Typography variant="body1" sx={{ fontWeight: 800, color: amountColor }}>
              {`${isIn ? '+' : '−'}${formatCurrency(ev.amount)}`}
            </Typography>
          ) : null}
          {ev.running_balance != null && ev.type !== 'counter_opened' && ev.type !== 'counter_closed' ? (
            <Typography variant="caption" color="text.secondary">
              Bal {formatCurrency(ev.running_balance)}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

function CloseCounterBreakdown({ session, expectedOnClose, isDark }) {
  const opening = session?.opening_breakdown || {};
  const collection = session?.collection_breakdown || {};
  const hotelOpening = Number(opening.hotel || 0);
  const restaurantOpening = Number(opening.restaurant || 0);
  const openingTotal = Number(opening.total ?? session?.opening_cash ?? 0);
  const hotelCollected = Number(collection?.hotel?.collected || 0);
  const restaurantCollected = Number(collection?.restaurant?.collected || 0);
  const totalCollected = Number(collection?.total_collected ?? session?.cash_collected ?? 0);
  const totalRefunds = Number(collection?.total_refunds || 0);
  const withdrawn = Number(session?.cash_withdrawn || 0);

  const rowSx = {
    display: 'grid',
    gridTemplateColumns: '1.4fr repeat(3, 1fr)',
    gap: 1,
    alignItems: 'center',
    py: 0.75,
    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
  };

  const renderAmount = (value, accent = false) => (
    <Typography
      variant="body2"
      sx={{
        fontWeight: accent ? 800 : 600,
        color: accent ? '#4ce1b6' : (isDark ? '#e2e8f0' : '#334155'),
        textAlign: 'right',
      }}
    >
      {formatCurrency(value)}
    </Typography>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        borderColor: alpha('#4ce1b6', 0.35),
        bgcolor: isDark ? alpha('#4ce1b6', 0.08) : alpha('#4ce1b6', 0.06),
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.25 }}>
        Session Cash Breakdown
      </Typography>
      <Box sx={{ ...rowSx, borderBottom: 'none', pb: 0, pt: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Hotel</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Restaurant</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Total</Typography>
      </Box>
      <Box sx={rowSx}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Opening float</Typography>
        {renderAmount(hotelOpening)}
        {renderAmount(restaurantOpening)}
        {renderAmount(openingTotal, true)}
      </Box>
      <Box sx={rowSx}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Cash collected</Typography>
        {renderAmount(hotelCollected)}
        {renderAmount(restaurantCollected)}
        {renderAmount(totalCollected, true)}
      </Box>
      {totalRefunds > 0 ? (
        <Box sx={rowSx}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Refunds</Typography>
          <Typography variant="body2" sx={{ textAlign: 'right', color: '#f87171' }}>—</Typography>
          <Typography variant="body2" sx={{ textAlign: 'right', color: '#f87171' }}>—</Typography>
          {renderAmount(totalRefunds)}
        </Box>
      ) : null}
      {withdrawn > 0 ? (
        <Box sx={rowSx}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Withdrawn</Typography>
          <Typography variant="body2" sx={{ textAlign: 'right', color: 'text.secondary' }}>—</Typography>
          <Typography variant="body2" sx={{ textAlign: 'right', color: 'text.secondary' }}>—</Typography>
          {renderAmount(withdrawn)}
        </Box>
      ) : null}
      <Box sx={{ ...rowSx, borderBottom: 'none', pt: 1.25, mt: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>Expected in counter</Typography>
        <Typography variant="body2" sx={{ textAlign: 'right', color: 'text.secondary' }}>—</Typography>
        <Typography variant="body2" sx={{ textAlign: 'right', color: 'text.secondary' }}>—</Typography>
        <Typography variant="h6" sx={{ fontWeight: 900, color: '#4ce1b6', textAlign: 'right' }}>
          {formatCurrency(expectedOnClose)}
        </Typography>
      </Box>
    </Paper>
  );
}

function LedgerEntrySessionSummary({ sessionSummary, isDark }) {
  if (!sessionSummary) return null;

  const opening = sessionSummary.opening_breakdown || {};
  const collection = sessionSummary.collection_breakdown || {};
  const hotelCollected = Number(collection?.hotel?.collected || 0);
  const restaurantCollected = Number(collection?.restaurant?.collected || 0);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        borderColor: alpha('#6366f1', isDark ? 0.35 : 0.28),
        bgcolor: isDark ? alpha('#6366f1', 0.08) : alpha('#6366f1', 0.04),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.25}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Session Summary
        </Typography>
        {sessionSummary.shift ? (
          <Chip size="small" label={sessionSummary.shift} sx={{ fontWeight: 600 }} />
        ) : null}
      </Stack>
      <Stack spacing={1}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Opened</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {sessionSummary.opened_at
                ? formatCashCounterDateTime(sessionSummary.opened_at, getHotelTimezone())
                : (sessionSummary.opened_at_display || '—')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Closed</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {sessionSummary.closed_at
                ? formatCashCounterDateTime(sessionSummary.closed_at, getHotelTimezone())
                : (sessionSummary.closed_at_display || '—')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Total Hours</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: ACCENT }}>
              {sessionSummary.duration_label || '—'}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Opening Float</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(sessionSummary.opening_cash)}</Typography>
            <Typography variant="caption" color="text.secondary">
              Hotel {formatCurrency(opening.hotel)} · Restaurant {formatCurrency(opening.restaurant)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Cash Collected</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: ACCENT }}>
              {formatCurrency(sessionSummary.total_collected)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Hotel {formatCurrency(hotelCollected)} · Restaurant {formatCurrency(restaurantCollected)}
            </Typography>
          </Box>
        </Stack>
        {Number(sessionSummary.cash_withdrawn || 0) > 0 ? (
          <Typography variant="caption" color="text.secondary">
            Withdrawn during session: {formatCurrency(sessionSummary.cash_withdrawn)}
          </Typography>
        ) : null}
        {sessionSummary.closing_actual_cash != null ? (
          <Typography variant="caption" color="text.secondary">
            Cash counted at close: {formatCurrency(sessionSummary.closing_actual_cash)}
          </Typography>
        ) : null}
        {sessionSummary.cashier_name ? (
          <Typography variant="caption" color="text.secondary">
            Cashier: {sessionSummary.cashier_name}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function DrawerConfirmationOverlay({
  open,
  isDark,
  title,
  message,
  confirmLabel,
  onCancel,
  onConfirm,
  loading,
}) {
  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: alpha(isDark ? '#020617' : '#0f172a', isDark ? 0.72 : 0.28),
        backdropFilter: 'blur(10px)',
      }}
    >
      <Paper
        elevation={isDark ? 0 : 18}
        sx={{
          width: '100%',
          maxWidth: 430,
          position: 'relative',
          overflow: 'hidden',
          p: 2.5,
          borderRadius: 4,
          border: `1px solid ${alpha(ACCENT, isDark ? 0.34 : 0.28)}`,
          background: isDark
            ? `linear-gradient(145deg, ${alpha('#111827', 0.98)} 0%, ${alpha('#020617', 0.98)} 100%)`
            : 'linear-gradient(145deg, #ffffff 0%, #f8fffc 100%)',
          boxShadow: isDark
            ? `0 24px 70px ${alpha('#000', 0.55)}, inset 0 1px 0 ${alpha('#fff', 0.06)}`
            : `0 24px 70px ${alpha('#0f172a', 0.22)}`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 180,
            height: 180,
            right: -70,
            top: -80,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(ACCENT, 0.26)} 0%, ${alpha(ACCENT, 0)} 68%)`,
          }}
        />
        <Stack spacing={2.25} sx={{ position: 'relative' }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: 3,
                display: 'grid',
                placeItems: 'center',
                color: ACCENT,
                bgcolor: alpha(ACCENT, isDark ? 0.16 : 0.12),
                border: `1px solid ${alpha(ACCENT, isDark ? 0.36 : 0.26)}`,
              }}
            >
              <WarningAmberOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a' }}>
                {title}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.6 }}>
                {message}
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
              sx={{
                py: 1.1,
                borderRadius: 2,
                fontWeight: 800,
                borderColor: alpha(ACCENT, isDark ? 0.32 : 0.28),
                color: isDark ? '#cbd5e1' : '#334155',
                '&:hover': {
                  borderColor: ACCENT,
                  bgcolor: alpha(ACCENT, isDark ? 0.1 : 0.08),
                },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={onConfirm}
              disabled={loading}
              sx={{
                py: 1.1,
                borderRadius: 2,
                fontWeight: 900,
                bgcolor: ACCENT,
                color: '#04120e',
                boxShadow: `0 12px 28px ${alpha(ACCENT, 0.28)}`,
                '&:hover': { bgcolor: '#58f2ce' },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#04120e' }} /> : confirmLabel}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

function SummaryCard({ title, value, subtitle, icon: Icon, accent, isDark, highlight }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: `1px solid ${alpha(ACCENT, isDark ? 0.18 : 0.22)}`,
        background: highlight
          ? `linear-gradient(145deg, ${alpha(ACCENT, isDark ? 0.22 : 0.14)} 0%, ${alpha('#ffffff', isDark ? 0.04 : 0.95)} 100%)`
          : isDark
            ? alpha('#ffffff', 0.03)
            : '#fff',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 28px ${alpha(ACCENT, 0.12)}` },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ flex: 1, width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, letterSpacing: 0.4 }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: accent || ACCENT }}>
            {value}
          </Typography>
          <Box sx={{ flex: 1, minHeight: 32, mt: 0.5 }}>
            {subtitle && (
              <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', display: 'block', lineHeight: 1.45 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {Icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(ACCENT, 0.12),
              color: ACCENT,
            }}
          >
            <Icon fontSize="small" />
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

const PDF_MARGIN = 14;
const PDF_EMPTY_CELL = '\u2014';

/** jsPDF Helvetica cannot render ₹; use Rs. prefix in exported PDFs. */
function formatCurrencyForPdf(value) {
  const num = parseFloat(value);
  const safe = Number.isFinite(num) ? num : 0;
  return `Rs.${safe.toFixed(2)}`;
}

function drawCashCounterPdfHeader(pdf, title, hotel, generatedAt) {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(15, 23, 42);
  pdf.text(title, PDF_MARGIN, 18);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  const metaParts = [hotel, generatedAt ? `Generated ${generatedAt}` : null].filter(Boolean);
  pdf.text(metaParts.join(' \u00B7 '), PDF_MARGIN, 25);
}

function drawCashCounterSummaryCards(pdf, summary, startY) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const contentWidth = pageWidth - PDF_MARGIN * 2;
  const gap = 5;
  const cardWidth = (contentWidth - gap * 2) / 3;
  const cardHeight = 24;
  const cards = [
    { label: 'CASH AVAILABLE', value: summary?.cash_available },
    { label: 'COLLECTED', value: summary?.cash_collected },
    { label: 'WITHDRAWN', value: summary?.cash_withdrawn },
  ];

  cards.forEach((card, index) => {
    const x = PDF_MARGIN + index * (cardWidth + gap);
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.3);
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(x, startY, cardWidth, cardHeight, 2.5, 2.5, 'FD');

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(100, 116, 139);
    pdf.text(card.label, x + 4, startY + 8);

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(15, 23, 42);
    pdf.text(formatCurrencyForPdf(card.value), x + 4, startY + 18);
  });

  return startY + cardHeight + 10;
}

function buildCashCounterPdf(data, title) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const hotelTimezone = data.hotel_timezone || data.summary?.hotel_timezone || getHotelTimezone();
  const generatedAt = data.generated_at
    ? formatCashCounterDateTime(data.generated_at, hotelTimezone)
    : (data.generated_at_display
      || formatCashCounterDateTime(getHotelNowMoment(hotelTimezone).toISOString(), hotelTimezone));

  drawCashCounterPdfHeader(pdf, title, data.hotel || '', generatedAt);
  const tableStartY = drawCashCounterSummaryCards(pdf, data.summary, 32);

  autoTable(pdf, {
    startY: tableStartY,
    theme: 'plain',
    head: [['Date/Time', 'Description', 'Debit', 'Credit', 'Balance']],
    body: (data.ledger || []).map((r) => {
      const when = formatCashCounterLedgerWhen(r, hotelTimezone);
      return [
        `${when.date || ''} ${when.time || ''}`.trim(),
        r.description || '',
        r.debit ? formatCurrencyForPdf(r.debit) : PDF_EMPTY_CELL,
        r.credit ? formatCurrencyForPdf(r.credit) : PDF_EMPTY_CELL,
        formatCurrencyForPdf(r.running_balance),
      ];
    }),
    styles: {
      fontSize: 8,
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      textColor: [15, 23, 42],
      lineColor: [226, 232, 240],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [248, 250, 252],
      textColor: [100, 116, 139],
      fontStyle: 'bold',
      lineWidth: 0.2,
      lineColor: [226, 232, 240],
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: 32 },
      1: { cellWidth: 'auto' },
      2: { halign: 'right', cellWidth: 28 },
      3: { halign: 'right', cellWidth: 28 },
      4: { halign: 'right', cellWidth: 28, fontStyle: 'bold' },
    },
    margin: { left: PDF_MARGIN, right: PDF_MARGIN },
  });

  return pdf;
}

async function downloadCashCounterPdf(data, title) {
  const hotelTimezone = data.hotel_timezone || data.summary?.hotel_timezone || getHotelTimezone();
  const filename = `${title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'Cash-Counter'}-${getHotelNowMoment(hotelTimezone).format('YYYY-MM-DD')}.pdf`;
  const pdf = buildCashCounterPdf(data, title);
  await downloadBlob(pdf.output('blob'), filename);
}

export default function CashCounterPanel() {
  const isDark = detectThemeSync() === 'dark';
  const [range, setRange] = useState('today');
  const [customStart, setCustomStart] = useState(() => getHotelNowMoment());
  const [customEnd, setCustomEnd] = useState(() => getHotelNowMoment());
  const [loading, setLoading] = useState(true);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerPage, setLedgerPage] = useState(0);
  const [ledgerRowsPerPage, setLedgerRowsPerPage] = useState(10);
  const [timeline, setTimeline] = useState([]);
  const [timelinePage, setTimelinePage] = useState(0);
  const [timelineRowsPerPage, setTimelineRowsPerPage] = useState(5);
  const [bottomTab, setBottomTab] = useState(0); // 0 = Timeline (primary), 1 = Cash Ledger
  const [filterEntryType, setFilterEntryType] = useState('');
  const [filterShift, setFilterShift] = useState('');
  const [ledgerAction, setLedgerAction] = useState(null);
  const [selectedLedgerEntry, setSelectedLedgerEntry] = useState(null);
  const [ledgerEditForm, setLedgerEditForm] = useState({ description: '', debit: '', credit: '' });

  const [openDialog, setOpenDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [convertDialog, setConvertDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const [openForm, setOpenForm] = useState({
    opening_hotel_cash: '',
    opening_restaurant_cash: '',
    shift: getSuggestedShift(),
    remarks: '',
  });
  const [closeForm, setCloseForm] = useState({
    actual_cash: '',
    withdrawal_amount: '',
    withdrawal_reason: '',
    reason: '',
    notes: '',
    reopen_after_close: true,
  });
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', reason: '', approved_by: '', remarks: '' });
  const [convertForm, setConvertForm] = useState({ amount: '', target_method: 'upi', notes: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const dateQueryParams = useMemo(() => {
    const p = { range };
    if (range === 'custom') {
      p.start_date = customStart.format('YYYY-MM-DD');
      p.end_date = customEnd.format('YYYY-MM-DD');
    }
    return p;
  }, [range, customStart, customEnd]);

  const ledgerQueryParams = useMemo(() => {
    const p = { ...dateQueryParams };
    if (filterEntryType) p.entry_type = filterEntryType;
    if (filterShift) p.shift = filterShift;
    return p;
  }, [dateQueryParams, filterEntryType, filterShift]);

  const loadSummaryData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    setError('');
    try {
      const summaryData = await getCashCounterSummary(dateQueryParams);
      setSummary(summaryData);
    } catch (err) {
      if (!silent) {
        setError(err?.response?.data?.error || err?.message || 'Failed to load cash counter summary.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [dateQueryParams]);

  const loadLedgerData = useCallback(async () => {
    setLedgerLoading(true);
    setError('');
    try {
      const [ledgerData, timelineData] = await Promise.all([
        getCashCounterLedger(ledgerQueryParams),
        getCashCounterTimeline(ledgerQueryParams),
      ]);
      setLedger(ledgerData.entries || []);
      setTimeline(timelineData.events || []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load cash counter ledger.');
    } finally {
      setLedgerLoading(false);
    }
  }, [ledgerQueryParams]);

  const loadData = useCallback(async () => {
    await Promise.all([loadSummaryData(), loadLedgerData()]);
  }, [loadSummaryData, loadLedgerData]);

  useEffect(() => {
    loadSummaryData();
  }, [loadSummaryData]);

  useEffect(() => {
    loadLedgerData();
  }, [loadLedgerData]);

  useEffect(() => {
    setLedgerPage(0);
    setTimelinePage(0);
  }, [range, customStart, customEnd, filterEntryType, filterShift]);

  const session = summary?.active_session;
  const isOpen = summary?.counter_status === 'open';
  const canOperate = summary?.can_operate;
  const hotelTimezone = summary?.hotel_timezone || session?.hotel_timezone || getHotelTimezone();
  const suggestedShift = summary?.suggested_shift || getSuggestedShift(getHotelNowMoment(hotelTimezone));

  const openingBreakdown = summary?.opening_breakdown;
  const collectionBreakdown = summary?.collection_breakdown;
  const displayedCashCollected = Number(
    collectionBreakdown?.total_collected ?? summary?.cash_collected ?? 0,
  );
  const displayedCashRefunds = Number(
    summary?.cash_refunds ?? collectionBreakdown?.total_refunds ?? 0,
  );
  const displayedCashAdjustments = Number(summary?.cash_adjustments ?? 0);
  const currentCashSubtitle = useMemo(() => {
    const isLiveSummary = summary?.is_live_summary !== false && summary?.counter_status === 'open';

    if (!isLiveSummary) {
      const parts = [`Selected period`];
      if (Number(summary?.opening_cash || 0) > 0) {
        parts.push(`· Opening ${formatCurrency(summary.opening_cash)}`);
      }
      parts.push(`· Collected ${formatCurrency(displayedCashCollected)}`);
      if (Number(summary?.cash_withdrawn || 0) > 0) {
        parts.push(`· Withdrawn ${formatCurrency(summary.cash_withdrawn)}`);
      }
      if (displayedCashRefunds > 0) {
        parts.push(`· Refunds ${formatCurrency(displayedCashRefunds)}`);
      }
      return parts.join(' ');
    }

    if (summary?.counter_status === 'closed') {
      const parts = ['Physical cash counted after last counter close'];
      if (displayedCashCollected > 0) {
        parts.push(`· Collected ${formatCurrency(displayedCashCollected)}`);
      }
      if (Number(summary?.cash_withdrawn || 0) > 0) {
        parts.push(`· Withdrawn ${formatCurrency(summary.cash_withdrawn)}`);
      }
      if (displayedCashRefunds > 0) {
        parts.push(`· Refunds ${formatCurrency(displayedCashRefunds)}`);
      }
      const manualAdjustments = Number(summary?.cash_manual_adjustments ?? 0);
      if (manualAdjustments !== 0) {
        parts.push(`· Reconciliation ${formatCurrency(Math.abs(manualAdjustments))}`);
      }
      return parts.join(' ');
    }

    const parts = [
      `Session total since open`,
      `· Opening ${formatCurrency(summary?.opening_cash)}`,
      `· Collected ${formatCurrency(displayedCashCollected)}`,
    ];
    if (displayedCashRefunds > 0) {
      parts.push(`· Refunds ${formatCurrency(displayedCashRefunds)}`);
    }
    parts.push(`· Withdrawn ${formatCurrency(summary?.cash_withdrawn)}`);
    if (displayedCashAdjustments !== 0) {
      parts.push(`· Adjustments ${formatCurrency(displayedCashAdjustments)}`);
    }
    return parts.join(' ');
  }, [
    displayedCashAdjustments,
    displayedCashCollected,
    displayedCashRefunds,
    summary?.cash_manual_adjustments,
    summary?.cash_withdrawn,
    summary?.counter_status,
    summary?.is_live_summary,
    summary?.opening_cash,
  ]);
  const openFormTotal = (parseFloat(openForm.opening_hotel_cash) || 0) + (parseFloat(openForm.opening_restaurant_cash) || 0);
  const expectedOnClose = session?.current_cash ?? summary?.expected_close_cash ?? summary?.cash_available ?? 0;
  const closeWithdrawalAmount = parseFloat(closeForm.withdrawal_amount) || 0;
  const closeWithdrawalProvided = String(closeForm.withdrawal_amount ?? '').trim() !== '';
  const closeWithdrawalInvalid = !closeWithdrawalProvided
    || closeWithdrawalAmount < 0
    || closeWithdrawalAmount > expectedOnClose;
  const closeFormValid = closeWithdrawalProvided
    && closeForm.actual_cash !== ''
    && closeWithdrawalAmount >= 0
    && closeWithdrawalAmount <= expectedOnClose;
  const expectedAfterCloseWithdrawal = Math.max(0, expectedOnClose - closeWithdrawalAmount);
  const cashLeftInCounter = parseFloat(closeForm.actual_cash) || 0;
  const reopenSplit = useMemo(
    () => splitCashByOpeningBreakdown(cashLeftInCounter, session?.opening_breakdown),
    [cashLeftInCounter, session?.opening_breakdown],
  );
  const fieldSx = useMemo(() => getBookingDrawerTextFieldSx(isDark), [isDark]);
  const drawerSelectMenuProps = useMemo(() => getDrawerSelectMenuProps(isDark), [isDark]);
  const paginatedLedger = useMemo(
    () => ledger.slice(ledgerPage * ledgerRowsPerPage, ledgerPage * ledgerRowsPerPage + ledgerRowsPerPage),
    [ledger, ledgerPage, ledgerRowsPerPage],
  );
  const timelineGroups = useMemo(() => groupTimelineBySession(timeline), [timeline]);
  const paginatedTimelineGroups = useMemo(
    () => timelineGroups.slice(
      timelinePage * timelineRowsPerPage,
      timelinePage * timelineRowsPerPage + timelineRowsPerPage,
    ),
    [timelineGroups, timelinePage, timelineRowsPerPage],
  );

  useEffect(() => {
    if (!openDialog) return;
    const defaults = resolveOpenCounterDefaults(summary);
    setOpenForm({
      ...defaults,
      shift: summary?.suggested_shift || getSuggestedShift(getHotelNowMoment(summary?.hotel_timezone || getHotelTimezone())),
      remarks: '',
    });
  }, [openDialog, summary?.suggested_opening, summary?.cash_available, summary?.opening_breakdown, summary?.suggested_shift, summary?.hotel_timezone]);

  useEffect(() => {
    if (!closeDialog) return;
    setCloseForm({
      actual_cash: expectedOnClose != null && expectedOnClose !== '' ? String(Number(expectedOnClose).toFixed(2)) : '',
      withdrawal_amount: '',
      withdrawal_reason: '',
      reason: '',
      notes: '',
      reopen_after_close: true,
    });
  }, [closeDialog, expectedOnClose]);

  useEffect(() => {
    if (!withdrawDialog) return;
    setWithdrawForm({ amount: '', reason: '', approved_by: '', remarks: '' });
  }, [withdrawDialog]);

  useEffect(() => {
    if (!convertDialog) return;
    setConvertForm({ amount: '', target_method: 'upi', notes: '' });
  }, [convertDialog]);

  useEffect(() => {
    setLedgerPage(0);
  }, [ledger]);

  useEffect(() => {
    setTimelinePage(0);
  }, [timeline]);

  const handleLedgerRowsPerPageChange = (event) => {
    setLedgerRowsPerPage(parseInt(event.target.value, 10));
    setLedgerPage(0);
  };

  const handleTimelineRowsPerPageChange = (event) => {
    setTimelineRowsPerPage(parseInt(event.target.value, 10));
    setTimelinePage(0);
  };

  const handleOpenCounter = async () => {
    setActionLoading(true);
    try {
      await openCashCounter({
        opening_hotel_cash: parseFloat(openForm.opening_hotel_cash) || 0,
        opening_restaurant_cash: parseFloat(openForm.opening_restaurant_cash) || 0,
        shift: openForm.shift,
        remarks: openForm.remarks,
      });
      setOpenDialog(false);
      setOpenForm({
        opening_hotel_cash: '',
        opening_restaurant_cash: '',
        shift: suggestedShift,
        remarks: '',
      });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to open counter.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseCounter = async () => {
    if (!session?.id || !closeFormValid) return;
    setActionLoading(true);
    try {
      await closeCashCounter(session.id, {
        actual_cash: parseFloat(closeForm.actual_cash) || 0,
        withdrawal_amount: closeWithdrawalAmount,
        withdrawal_reason: closeForm.withdrawal_reason,
        reason: closeForm.reason,
        notes: closeForm.notes,
        reopen_after_close: Boolean(closeForm.reopen_after_close),
        reopen_shift: session?.shift || suggestedShift,
      });
      setCloseDialog(false);
      setCloseForm({
        actual_cash: '',
        withdrawal_amount: '',
        withdrawal_reason: '',
        reason: '',
        notes: '',
        reopen_after_close: true,
      });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to close counter.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = async () => {
    setActionLoading(true);
    try {
      await withdrawCashCounter({
        amount: parseFloat(withdrawForm.amount) || 0,
        reason: withdrawForm.reason,
        approved_by: withdrawForm.approved_by,
        remarks: withdrawForm.remarks,
      });
      setWithdrawDialog(false);
      setWithdrawForm({ amount: '', reason: '', approved_by: '', remarks: '' });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to withdraw cash.');
    } finally {
      setActionLoading(false);
    }
  };

  const convertAmount = parseFloat(convertForm.amount) || 0;
  const convertValid = convertAmount > 0
    && convertAmount <= (summary?.cash_available || 0)
    && convertForm.target_method
    && convertForm.target_method !== 'cash';

  const handleConvert = async () => {
    if (!convertValid) return;
    setActionLoading(true);
    try {
      await convertCashCounterPayment({
        amount: convertAmount,
        target_method: convertForm.target_method,
        notes: convertForm.notes,
      });
      setConfirmAction(null);
      setConvertDialog(false);
      setConvertForm({ amount: '', target_method: 'upi', notes: '' });
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to convert cash.');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = async (type) => {
    try {
      const printParams = type === 'daily_summary' ? dateQueryParams : ledgerQueryParams;
      const data = await getCashCounterPrintData({
        ...printParams,
        type,
        session_id: session?.id,
      });
      const titles = {
        daily_summary: 'Daily Cash Summary',
        shift_closing: 'Shift Closing Report',
        ledger: 'Cash Ledger',
      };
      const title = titles[type] || 'Cash Report';
      await downloadCashCounterPdf(data, title);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to download PDF.');
    }
  };

  const openLedgerView = (entry) => {
    setSelectedLedgerEntry(entry);
    setLedgerAction('view');
  };

  const openLedgerEdit = (entry) => {
    setSelectedLedgerEntry(entry);
    setLedgerEditForm({
      description: entry.description || '',
      debit: entry.debit ? String(entry.debit) : '',
      credit: entry.credit ? String(entry.credit) : '',
    });
    setLedgerAction('edit');
  };

  const closeLedgerAction = () => {
    setLedgerAction(null);
    setSelectedLedgerEntry(null);
    setLedgerEditForm({ description: '', debit: '', credit: '' });
  };

  const handleLedgerEditSave = async () => {
    if (!selectedLedgerEntry) return;
    setActionLoading(true);
    try {
      await updateCashCounterLedgerEntry(selectedLedgerEntry.id, {
        description: ledgerEditForm.description,
        debit: parseFloat(ledgerEditForm.debit) || 0,
        credit: parseFloat(ledgerEditForm.credit) || 0,
      });
      closeLedgerAction();
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update ledger entry.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLedgerDelete = async () => {
    if (!selectedLedgerEntry) return;
    setActionLoading(true);
    try {
      await deleteCashCounterLedgerEntry(selectedLedgerEntry.id);
      closeLedgerAction();
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete ledger entry.');
    } finally {
      setActionLoading(false);
    }
  };

  const remainingAfterWithdraw = (parseFloat(withdrawForm.amount) || 0) > 0
    ? (summary?.cash_available || 0) - (parseFloat(withdrawForm.amount) || 0)
    : summary?.cash_available;
  const remainingAfterConvert = convertAmount > 0
    ? (summary?.cash_available || 0) - convertAmount
    : summary?.cash_available;

  const applySuggestedOpening = () => {
    const defaults = resolveOpenCounterDefaults(summary);
    if (!defaults.opening_hotel_cash && !defaults.opening_restaurant_cash) return;
    setOpenForm((prev) => ({
      ...prev,
      ...defaults,
    }));
  };

  const handleOpenCounterDialog = () => {
    setOpenDialog(true);
    void loadSummaryData({ silent: true });
  };

  const handleConfirmedAction = async () => {
    const action = confirmAction;
    if (!action) return;

    if (action === 'open') await handleOpenCounter();
    if (action === 'close') await handleCloseCounter();
    if (action === 'withdraw') await handleWithdraw();
    if (action === 'convert') await handleConvert();
    setConfirmAction(null);
  };

  const renderConfirmationOverlay = (action) => {
    const copy = CONFIRM_ACTION_COPY[action];
    return (
      <DrawerConfirmationOverlay
        open={confirmAction === action}
        isDark={isDark}
        title={copy.title}
        message={copy.message}
        confirmLabel={copy.confirmLabel}
        loading={actionLoading}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleConfirmedAction}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Toolbar */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: `1px solid ${alpha(ACCENT, isDark ? 0.15 : 0.2)}`,
            bgcolor: isDark ? alpha('#fff', 0.02) : '#fff',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {DATE_RANGES.map((r) => (
                <Chip
                  key={r.key}
                  label={r.label}
                  onClick={() => setRange(r.key)}
                  color={range === r.key ? 'primary' : 'default'}
                  variant={range === r.key ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 700,
                    ...(range === r.key && {
                      bgcolor: ACCENT,
                      color: '#042018',
                      '&:hover': { bgcolor: '#58f2ce' },
                    }),
                  }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {range === 'custom' && (
                <>
                  <DatePicker label="From" value={customStart} onChange={(v) => v && setCustomStart(v)} slotProps={{ textField: { size: 'small' } }} />
                  <DatePicker label="To" value={customEnd} onChange={(v) => v && setCustomEnd(v)} slotProps={{ textField: { size: 'small' } }} />
                </>
              )}
              <Tooltip title="Refresh">
                <IconButton onClick={loadData} disabled={loading || ledgerLoading}>
                  <RefreshOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={() => handlePrint('daily_summary')}
              >
                Download PDF
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Summary cards */}
        {loading ? (
          <Grid container spacing={2} alignItems="stretch">
            {[1, 2, 3, 4, 5].map((i) => (
              <Grid item xs={12} sm={6} md={i === 1 ? 4 : 2} key={i} sx={{ display: 'flex' }}>
                <Skeleton variant="rounded" height="100%" sx={{ borderRadius: 3, width: '100%', minHeight: 118 }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <SummaryCard
                title="Current Cash Available"
                value={formatCurrency(summary?.cash_available)}
                subtitle={currentCashSubtitle}
                icon={AccountBalanceWalletOutlinedIcon}
                isDark={isDark}
                highlight
              />
            </Grid>
            <Grid item xs={6} md={2} sx={{ display: 'flex' }}>
              <SummaryCard
                title="Opening Cash"
                value={formatCurrency(openingBreakdown?.total ?? summary?.opening_cash)}
                subtitle={
                  openingBreakdown
                    ? `Hotel ${formatCurrency(openingBreakdown.hotel)} · Restaurant ${formatCurrency(openingBreakdown.restaurant)}`
                    : 'Hotel + Restaurant float'
                }
                isDark={isDark}
              />
            </Grid>
            <Grid item xs={6} md={2} sx={{ display: 'flex' }}>
              <SummaryCard
                title="Cash Collected"
                value={formatCurrency(displayedCashCollected)}
                subtitle={
                  collectionBreakdown
                    ? `Hotel ${formatCurrency(collectionBreakdown.hotel?.collected)} · Restaurant ${formatCurrency(collectionBreakdown.restaurant?.collected)}`
                    : 'Hotel + Restaurant cash'
                }
                isDark={isDark}
                accent="#22c55e"
              />
            </Grid>
            <Grid item xs={6} md={2} sx={{ display: 'flex' }}>
              <SummaryCard title="Cash Withdrawn" value={formatCurrency(summary?.cash_withdrawn)} isDark={isDark} accent="#f97316" />
            </Grid>
            <Grid item xs={6} md={2} sx={{ display: 'flex' }}>
              <SummaryCard
                title="Counter Status"
                value={isOpen ? 'Open' : 'Closed'}
                subtitle={session ? `${session.shift} · ${session.cashier_name}` : 'No active session'}
                icon={isOpen ? LockOpenOutlinedIcon : LockOutlinedIcon}
                isDark={isDark}
                accent={isOpen ? ACCENT : '#ef4444'}
              />
            </Grid>
          </Grid>
        )}

        <CashCollectionBreakdown
          breakdown={summary?.collection_breakdown}
          openingBreakdown={summary?.opening_breakdown}
          isDark={isDark}
          loading={loading}
        />

        {/* Session info + actions */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: `1px solid ${alpha(ACCENT, isDark ? 0.15 : 0.2)}`,
            bgcolor: isDark ? alpha('#fff', 0.02) : '#fff',
          }}
        >
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ lg: 'center' }}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                Current Shift
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {session?.shift || '—'}
              </Typography>
              <Stack direction="row" spacing={2} mt={1} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  Cashier: <strong>{session?.cashier_name || '—'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Opened:{' '}
                  <strong>
                    {session?.opened_at
                      ? formatCashCounterDateTime(session.opened_at, hotelTimezone)
                      : (session?.opened_at_display || '—')}
                  </strong>
                </Typography>
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {summary?.can_open && (
                <Button variant="contained" startIcon={<LockOpenOutlinedIcon />} onClick={handleOpenCounterDialog} sx={{ bgcolor: ACCENT, color: '#042018', '&:hover': { bgcolor: '#58f2ce' } }}>
                  Open Counter
                </Button>
              )}
              {summary?.can_close && (
                <Button variant="outlined" color="warning" startIcon={<LockOutlinedIcon />} onClick={() => setCloseDialog(true)}>
                  Close Counter
                </Button>
              )}
              <Button variant="outlined" startIcon={<RemoveCircleOutlineOutlinedIcon />} disabled={!canOperate} onClick={() => setWithdrawDialog(true)}>
                Withdraw Cash
              </Button>
              <Button variant="outlined" startIcon={<SwapHorizOutlinedIcon />} onClick={() => setConvertDialog(true)}>
                Convert Cash
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Filters */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Transaction Type</InputLabel>
            <Select value={filterEntryType} label="Transaction Type" onChange={(e) => setFilterEntryType(e.target.value)}>
              {ENTRY_TYPES.map((t) => (
                <MenuItem key={t.value || 'all'} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Shift</InputLabel>
            <Select value={filterShift} label="Shift" onChange={(e) => setFilterShift(e.target.value)}>
              <MenuItem value="">All Shifts</MenuItem>
              {CASH_COUNTER_SHIFTS.map((shift) => (
                <MenuItem key={shift.value} value={shift.value}>
                  {shift.label} ({shift.timeRange})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Timeline (primary) / Ledger tabs */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(ACCENT, isDark ? 0.12 : 0.18)}`, overflow: 'hidden' }}>
          <Tabs value={bottomTab} onChange={(_, v) => setBottomTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<TimelineOutlinedIcon />} iconPosition="start" label="Timeline" />
            <Tab icon={<ReceiptLongOutlinedIcon />} iconPosition="start" label="Cash Ledger" />
          </Tabs>
          <Box sx={{ p: 2, minHeight: 280 }}>
            {ledgerLoading ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: ACCENT }} /></Box>
            ) : bottomTab === 0 ? (
              timelineGroups.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={6}>No activity for the selected period.</Typography>
              ) : (
                <>
                  <Stack spacing={1.75}>
                    {paginatedTimelineGroups.map((group, idx) => (
                      group.kind === 'session' ? (
                        <SessionTimelineCard
                          key={`session-${group.sessionId}-${idx}`}
                          group={group}
                          isDark={isDark}
                          hotelTimezone={hotelTimezone}
                        />
                      ) : (
                        <TimelineEventRow
                          key={`orphan-${group.event?.at}-${group.event?.ledger_entry_id || idx}`}
                          ev={group.event}
                          isDark={isDark}
                          hotelTimezone={hotelTimezone}
                        />
                      )
                    ))}
                  </Stack>
                  <TablePagination
                    component="div"
                    count={timelineGroups.length}
                    page={timelinePage}
                    rowsPerPage={timelineRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    onPageChange={(_, page) => setTimelinePage(page)}
                    onRowsPerPageChange={handleTimelineRowsPerPageChange}
                    sx={{
                      mt: 1,
                      borderTop: `1px solid ${alpha(ACCENT, isDark ? 0.12 : 0.1)}`,
                      color: isDark ? '#cbd5e1' : '#475569',
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        m: 0,
                      },
                      '& .MuiSvgIcon-root': {
                        color: isDark ? '#cbd5e1' : '#475569',
                      },
                    }}
                  />
                </>
              )
            ) : ledger.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={6}>No ledger entries for the selected period.</Typography>
            ) : (
              <>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: `1px solid ${alpha(ACCENT, 0.15)}` }}>
                        {['Date', 'Time', 'Description', 'Debit', 'Credit', 'Balance', 'By', 'Actions'].map((h) => (
                          <th key={h} style={{ padding: '10px 8px', fontWeight: 700, color: isDark ? '#94a3b8' : '#64748b' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedLedger.map((row) => {
                        const when = formatCashCounterLedgerWhen(row, hotelTimezone);
                        return (
                        <tr key={row.id} style={{ borderBottom: `1px solid ${alpha('#000', 0.06)}` }}>
                          <td style={{ padding: '10px 8px' }}>{when.date}</td>
                          <td style={{ padding: '10px 8px' }}>{when.time}</td>
                          <td style={{ padding: '10px 8px' }}>{row.description}</td>
                          <td style={{ padding: '10px 8px', color: '#ef4444' }}>{row.debit ? formatCurrency(row.debit) : '—'}</td>
                          <td style={{ padding: '10px 8px', color: '#22c55e' }}>{row.credit ? formatCurrency(row.credit) : '—'}</td>
                          <td style={{ padding: '10px 8px', fontWeight: 700 }}>{formatCurrency(row.running_balance)}</td>
                          <td style={{ padding: '10px 8px' }}>{row.created_by}</td>
                          <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                            <Tooltip title="View details">
                              <IconButton size="small" onClick={() => openLedgerView(row)}>
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={row.can_edit ? 'Edit entry' : 'System entry cannot be edited here'}>
                              <span>
                                <IconButton size="small" onClick={() => openLedgerEdit(row)} disabled={!row.can_edit}>
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={row.can_delete ? 'Delete entry' : 'System entry cannot be deleted here'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedLedgerEntry(row);
                                    setLedgerAction('delete');
                                  }}
                                  disabled={!row.can_delete}
                                  sx={{ color: row.can_delete ? '#ef4444' : undefined }}
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
                <TablePagination
                  component="div"
                  count={ledger.length}
                  page={ledgerPage}
                  rowsPerPage={ledgerRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  onPageChange={(_, page) => setLedgerPage(page)}
                  onRowsPerPageChange={handleLedgerRowsPerPageChange}
                  sx={{
                    mt: 1,
                    borderTop: `1px solid ${alpha(ACCENT, isDark ? 0.12 : 0.1)}`,
                    color: isDark ? '#cbd5e1' : '#475569',
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      m: 0,
                    },
                    '& .MuiSvgIcon-root': {
                      color: isDark ? '#cbd5e1' : '#475569',
                    },
                  }}
                />
              </>
            )}
          </Box>
          <Divider />
          <Stack direction="row" spacing={1} p={2} justifyContent="flex-end">
            <Button
              size="small"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() => handlePrint('ledger')}
            >
              Download Ledger PDF
            </Button>
            {session?.id && (
              <Button
                size="small"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={() => handlePrint('shift_closing')}
              >
                Download Shift Closing PDF
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Open Counter Drawer */}
        <CashCounterDrawerShell
          open={openDialog}
          onClose={() => {
            if (actionLoading) return;
            setConfirmAction(null);
            setOpenDialog(false);
          }}
          title="Open Counter"
          icon={LockOpenOutlinedIcon}
          isDark={isDark}
          disableClose={actionLoading || confirmAction === 'open'}
          footer={(
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setOpenDialog(false)}
                disabled={actionLoading}
                sx={{ ...getBookingDrawerOutlinedButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setConfirmAction('open')}
                disabled={actionLoading}
                sx={{ ...getBookingDrawerPrimaryButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                Open Counter
              </Button>
            </>
          )}
          overlay={renderConfirmationOverlay('open')}
        >
          <Stack spacing={2.5}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Opening cash is prefilled from current cash in the counter, split by hotel and restaurant float.
            </Alert>
            {(Number(summary?.suggested_opening?.total || 0) > 0 || Number(summary?.cash_available || 0) > 0) && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {summary?.suggested_opening?.source === 'last_closed_session'
                    ? <>Last close balance: <strong>{formatCurrency(summary.suggested_opening.total)}</strong></>
                    : <>Current cash available: <strong>{formatCurrency(summary?.suggested_opening?.total || summary?.cash_available)}</strong></>}
                </Typography>
                <Button size="small" onClick={applySuggestedOpening}>Reset to suggested</Button>
              </Stack>
            )}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Hotel Opening Cash"
                  type="number"
                  value={openForm.opening_hotel_cash}
                  onChange={(e) => setOpenForm({ ...openForm, opening_hotel_cash: e.target.value })}
                  fullWidth
                  inputProps={{ min: 0, step: '0.01' }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Restaurant Opening Cash"
                  type="number"
                  value={openForm.opening_restaurant_cash}
                  onChange={(e) => setOpenForm({ ...openForm, opening_restaurant_cash: e.target.value })}
                  fullWidth
                  inputProps={{ min: 0, step: '0.01' }}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: alpha(ACCENT, 0.35), bgcolor: alpha(ACCENT, 0.06) }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Total Opening Cash</Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, color: ACCENT }}>{formatCurrency(openFormTotal)}</Typography>
              </Stack>
            </Paper>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Shift</InputLabel>
              <Select
                value={openForm.shift}
                label="Shift"
                MenuProps={drawerSelectMenuProps}
                onChange={(e) => setOpenForm({ ...openForm, shift: e.target.value })}
              >
                {CASH_COUNTER_SHIFTS.map((shift) => (
                  <MenuItem key={shift.value} value={shift.value}>
                    {shift.label} ({shift.timeRange})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Auto-selected: {getShiftLabel(suggestedShift)}
            </Typography>
            <TextField label="Remarks" value={openForm.remarks} onChange={(e) => setOpenForm({ ...openForm, remarks: e.target.value })} fullWidth multiline rows={3} sx={fieldSx} />
          </Stack>
        </CashCounterDrawerShell>

        {/* Close Counter Drawer */}
        <CashCounterDrawerShell
          open={closeDialog}
          onClose={() => {
            if (actionLoading) return;
            setConfirmAction(null);
            setCloseDialog(false);
          }}
          title="Close Counter"
          icon={LockOutlinedIcon}
          isDark={isDark}
          disableClose={actionLoading || confirmAction === 'close'}
          footer={(
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setCloseDialog(false)}
                disabled={actionLoading}
                sx={{ ...getBookingDrawerOutlinedButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="warning"
                onClick={() => setConfirmAction('close')}
                disabled={actionLoading || !closeFormValid}
                sx={{ ...getBookingDrawerPrimaryButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                {closeForm.reopen_after_close ? 'Close & Open Counter' : 'Close Counter'}
              </Button>
            </>
          )}
          overlay={renderConfirmationOverlay('close')}
        >
          <Stack spacing={2.5}>
            <CloseCounterBreakdown session={session} expectedOnClose={expectedOnClose} isDark={isDark} />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                size="small"
                onClick={() => setCloseForm((prev) => ({
                  ...prev,
                  withdrawal_amount: String(Number(expectedOnClose).toFixed(2)),
                  actual_cash: '0.00',
                }))}
              >
                Withdraw full amount
              </Button>
            </Stack>
            <TextField
              label="Withdraw Before Close"
              type="number"
              required
              value={closeForm.withdrawal_amount}
              onChange={(e) => {
                const withdrawalAmount = parseFloat(e.target.value) || 0;
                const remainingCash = Math.max(0, expectedOnClose - withdrawalAmount);
                setCloseForm({
                  ...closeForm,
                  withdrawal_amount: e.target.value,
                  actual_cash: String(Number(remainingCash).toFixed(2)),
                });
              }}
              fullWidth
              inputProps={{ min: 0, max: expectedOnClose, step: '0.01' }}
              sx={fieldSx}
              helperText={
                !closeWithdrawalProvided
                  ? 'Required — enter the cash amount to withdraw before closing'
                  : `Cash remaining after withdrawal: ${formatCurrency(expectedAfterCloseWithdrawal)}`
              }
              error={closeWithdrawalInvalid}
            />
            {closeWithdrawalAmount > 0 && (
              <TextField
                label="Withdrawal Reason"
                value={closeForm.withdrawal_reason}
                onChange={(e) => setCloseForm({ ...closeForm, withdrawal_reason: e.target.value })}
                fullWidth
                sx={fieldSx}
                placeholder="Example: Shift close cash handover"
              />
            )}
            <TextField
              label="Cash Left in Counter"
              type="number"
              value={closeForm.actual_cash}
              onChange={(e) => setCloseForm({ ...closeForm, actual_cash: e.target.value })}
              fullWidth
              sx={fieldSx}
              helperText="Count the cash physically left in the counter after any closing withdrawal"
            />
            {closeForm.actual_cash !== '' && (
              <Alert severity={Math.abs(parseFloat(closeForm.actual_cash) - expectedAfterCloseWithdrawal) > 0.01 ? 'warning' : 'success'} sx={{ borderRadius: 2 }}>
                Difference: <strong>{formatCurrency(parseFloat(closeForm.actual_cash) - expectedAfterCloseWithdrawal)}</strong>
                {expectedAfterCloseWithdrawal > 0 && (
                  <> ({(((parseFloat(closeForm.actual_cash) - expectedAfterCloseWithdrawal) / expectedAfterCloseWithdrawal) * 100).toFixed(2)}%)</>
                )}
              </Alert>
            )}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderColor: alpha(ACCENT, closeForm.reopen_after_close ? 0.35 : 0.18),
                bgcolor: alpha(ACCENT, isDark ? 0.06 : 0.04),
              }}
            >
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={Boolean(closeForm.reopen_after_close)}
                    onChange={(event) => setCloseForm((prev) => ({
                      ...prev,
                      reopen_after_close: event.target.checked,
                    }))}
                    sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                  />
                )}
                label={(
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Close and open new counter with cash left
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Starts a fresh session using the cash left in counter as opening float
                    </Typography>
                  </Box>
                )}
              />
              {closeForm.reopen_after_close ? (
                <Box sx={{ mt: 1.25, pl: 4.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.75 }}>
                    Next opening float
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`Hotel ${formatCurrency(reopenSplit.hotel)}`} sx={{ fontWeight: 600 }} />
                    <Chip size="small" label={`Restaurant ${formatCurrency(reopenSplit.restaurant)}`} sx={{ fontWeight: 600 }} />
                    <Chip size="small" color="primary" label={`Total ${formatCurrency(reopenSplit.total)}`} sx={{ fontWeight: 700 }} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Shift: {getShiftLabel(session?.shift || suggestedShift)}
                  </Typography>
                </Box>
              ) : null}
            </Paper>
            <TextField label="Reason (if difference)" value={closeForm.reason} onChange={(e) => setCloseForm({ ...closeForm, reason: e.target.value })} fullWidth sx={fieldSx} />
            <TextField label="Notes" value={closeForm.notes} onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })} fullWidth multiline rows={3} sx={fieldSx} />
          </Stack>
        </CashCounterDrawerShell>

        {/* Withdraw Drawer */}
        <CashCounterDrawerShell
          open={withdrawDialog}
          onClose={() => {
            if (actionLoading) return;
            setConfirmAction(null);
            setWithdrawDialog(false);
          }}
          title="Withdraw Cash"
          icon={RemoveCircleOutlineOutlinedIcon}
          isDark={isDark}
          disableClose={actionLoading || confirmAction === 'withdraw'}
          footer={(
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setWithdrawDialog(false)}
                disabled={actionLoading}
                sx={{ ...getBookingDrawerOutlinedButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setConfirmAction('withdraw')}
                disabled={
                  actionLoading
                  || !withdrawForm.amount
                  || !withdrawForm.reason
                  || !withdrawForm.approved_by
                  || parseFloat(withdrawForm.amount) > (summary?.cash_available || 0)
                }
                sx={{ ...getBookingDrawerPrimaryButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                Withdraw
              </Button>
            </>
          )}
          overlay={renderConfirmationOverlay('withdraw')}
        >
          <Stack spacing={2.5}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: alpha(ACCENT, 0.35), bgcolor: alpha(ACCENT, isDark ? 0.08 : 0.06) }}>
              <Typography variant="caption" color="text.secondary">Cash available</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, color: ACCENT, mt: 0.5 }}>{formatCurrency(summary?.cash_available)}</Typography>
              <Button
                size="small"
                sx={{ mt: 1 }}
                onClick={() => setWithdrawForm((prev) => ({ ...prev, amount: String(Number(summary?.cash_available || 0).toFixed(2)) }))}
              >
                Withdraw full balance
              </Button>
            </Paper>
            <TextField label="Amount" type="number" value={withdrawForm.amount} onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })} fullWidth sx={fieldSx} />
            {withdrawForm.amount && (
              <Typography variant="body2">Remaining cash: <strong>{formatCurrency(remainingAfterWithdraw)}</strong></Typography>
            )}
            <TextField label="Reason" value={withdrawForm.reason} onChange={(e) => setWithdrawForm({ ...withdrawForm, reason: e.target.value })} fullWidth required sx={fieldSx} />
            <TextField label="Approved By" value={withdrawForm.approved_by} onChange={(e) => setWithdrawForm({ ...withdrawForm, approved_by: e.target.value })} fullWidth required sx={fieldSx} />
            <TextField label="Remarks" value={withdrawForm.remarks} onChange={(e) => setWithdrawForm({ ...withdrawForm, remarks: e.target.value })} fullWidth multiline rows={3} sx={fieldSx} />
          </Stack>
        </CashCounterDrawerShell>

        {/* Convert Cash Drawer */}
        <CashCounterDrawerShell
          open={convertDialog}
          onClose={() => {
            if (actionLoading) return;
            setConfirmAction(null);
            setConvertDialog(false);
          }}
          title="Convert Cash"
          icon={SwapHorizOutlinedIcon}
          isDark={isDark}
          disableClose={actionLoading || confirmAction === 'convert'}
          footer={(
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setConfirmAction(null);
                  setConvertDialog(false);
                }}
                disabled={actionLoading}
                sx={{ ...getBookingDrawerOutlinedButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setConfirmAction('convert')}
                disabled={actionLoading || !convertValid}
                sx={{ ...getBookingDrawerPrimaryButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                Convert Cash
              </Button>
            </>
          )}
          overlay={renderConfirmationOverlay('convert')}
        >
          <Stack spacing={2.5}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Convert physical cash from the counter into another method received from the other person. Example: give ₹1,000 cash and record ₹1,000 received by UPI.
            </Alert>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: alpha(ACCENT, 0.35), bgcolor: alpha(ACCENT, isDark ? 0.08 : 0.06) }}>
              <Typography variant="caption" color="text.secondary">Current cash available</Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, color: ACCENT, mt: 0.5 }}>{formatCurrency(summary?.cash_available)}</Typography>
            </Paper>
            <TextField
              label="Cash Amount to Convert"
              type="number"
              value={convertForm.amount}
              onChange={(e) => setConvertForm({ ...convertForm, amount: e.target.value })}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              sx={fieldSx}
              helperText="Cash will reduce by this amount"
            />
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Received Method</InputLabel>
              <Select
                value={convertForm.target_method}
                label="Received Method"
                MenuProps={drawerSelectMenuProps}
                onChange={(e) => setConvertForm({ ...convertForm, target_method: e.target.value })}
              >
                {PAYMENT_METHODS.filter((m) => m.value !== 'cash').map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {convertForm.amount && (
              <Typography variant="body2" color={remainingAfterConvert < 0 ? 'error.main' : 'text.primary'}>
                Cash after conversion: <strong>{formatCurrency(remainingAfterConvert)}</strong>
              </Typography>
            )}
            <TextField label="Notes" value={convertForm.notes} onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })} fullWidth multiline rows={3} sx={fieldSx} />
          </Stack>
        </CashCounterDrawerShell>

        <CashCounterDrawerShell
          open={ledgerAction === 'view'}
          onClose={closeLedgerAction}
          title="Ledger Entry Details"
          icon={VisibilityOutlinedIcon}
          isDark={isDark}
          footer={(
            <Button fullWidth variant="contained" onClick={closeLedgerAction} sx={getBookingDrawerPrimaryButtonSx(isDark)}>
              Close
            </Button>
          )}
        >
          {selectedLedgerEntry && (
            <Stack spacing={1.5}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: alpha(ACCENT, 0.35), bgcolor: alpha(ACCENT, isDark ? 0.08 : 0.06) }}>
                <Typography variant="caption" color="text.secondary">Entry Type</Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, color: ACCENT }}>{selectedLedgerEntry.entry_type_label}</Typography>
              </Paper>
              <LedgerEntrySessionSummary sessionSummary={selectedLedgerEntry.session_summary} isDark={isDark} />
              <Typography><strong>Entry Date/Time:</strong> {selectedLedgerEntry.date} {selectedLedgerEntry.time}</Typography>
              <Typography><strong>Description:</strong> {selectedLedgerEntry.description}</Typography>
              <Typography><strong>Debit:</strong> {formatCurrency(selectedLedgerEntry.debit)}</Typography>
              <Typography><strong>Credit:</strong> {formatCurrency(selectedLedgerEntry.credit)}</Typography>
              <Typography><strong>Balance:</strong> {formatCurrency(selectedLedgerEntry.running_balance)}</Typography>
              <Typography><strong>By:</strong> {selectedLedgerEntry.created_by}</Typography>
              {selectedLedgerEntry.reference && (
                <Typography><strong>Reference:</strong> {selectedLedgerEntry.reference}</Typography>
              )}
            </Stack>
          )}
        </CashCounterDrawerShell>

        <CashCounterDrawerShell
          open={ledgerAction === 'edit'}
          onClose={() => !actionLoading && closeLedgerAction()}
          title="Edit Ledger Entry"
          icon={EditOutlinedIcon}
          isDark={isDark}
          disableClose={actionLoading}
          footer={(
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={closeLedgerAction}
                disabled={actionLoading}
                sx={{ ...getBookingDrawerOutlinedButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleLedgerEditSave}
                disabled={actionLoading}
                sx={{ ...getBookingDrawerPrimaryButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                {actionLoading ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </>
          )}
        >
          <Stack spacing={2}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Editing this entry will recalculate later balances in the same counter session.
            </Alert>
            <TextField
              label="Description"
              value={ledgerEditForm.description}
              onChange={(e) => setLedgerEditForm({ ...ledgerEditForm, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              sx={fieldSx}
            />
            <TextField
              label="Debit"
              type="number"
              value={ledgerEditForm.debit}
              onChange={(e) => setLedgerEditForm({ ...ledgerEditForm, debit: e.target.value, credit: e.target.value ? '' : ledgerEditForm.credit })}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              sx={fieldSx}
            />
            <TextField
              label="Credit"
              type="number"
              value={ledgerEditForm.credit}
              onChange={(e) => setLedgerEditForm({ ...ledgerEditForm, credit: e.target.value, debit: e.target.value ? '' : ledgerEditForm.debit })}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              sx={fieldSx}
            />
          </Stack>
        </CashCounterDrawerShell>

        <CashCounterDrawerShell
          open={ledgerAction === 'delete'}
          onClose={() => !actionLoading && closeLedgerAction()}
          title="Delete Ledger Entry"
          icon={DeleteOutlineOutlinedIcon}
          isDark={isDark}
          disableClose={actionLoading}
          footer={(
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={closeLedgerAction}
                disabled={actionLoading}
                sx={{ ...getBookingDrawerOutlinedButtonSx(isDark), flex: 1, minWidth: 0 }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={handleLedgerDelete}
                disabled={actionLoading}
                sx={{ flex: 1, minWidth: 0, fontWeight: 800 }}
              >
                {actionLoading ? <CircularProgress size={20} /> : 'Delete'}
              </Button>
            </>
          )}
        >
          <Stack spacing={2}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              This will remove the selected correction entry and recalculate later balances.
            </Alert>
            {selectedLedgerEntry && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: alpha('#ef4444', 0.35), bgcolor: alpha('#ef4444', isDark ? 0.08 : 0.04) }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{selectedLedgerEntry.description}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Debit {formatCurrency(selectedLedgerEntry.debit)} · Credit {formatCurrency(selectedLedgerEntry.credit)}
                </Typography>
              </Paper>
            )}
          </Stack>
        </CashCounterDrawerShell>

      </Box>
    </LocalizationProvider>
  );
}
